import {computed, getCurrentInstance, onUnmounted, ref} from 'vue';
import type {MessageApi} from 'naive-ui';
import * as THREE from 'three';
import {
  BossExtrudeSceneResolver,
  convertMmToM,
  CutExtrudeDefinitionCoordinator,
  type BossExtrudeSceneContour,
  ExtrudeCutDefinitionSnapshotBuilder,
  ExtrudeFaceSelectionTargetResolver,
  ExtrudeEndTargetResolver,
  ExtrudeFeatureEditDefinitionFactory,
  type ExtrudeFeatureEditDefinition as ExtrudePropertyDefinition,
  FeatureEditSession,
  hasCurveProfileParams,
  hasCurveProfileRegionGeometry,
  isSketchGroup,
} from '@puppycad/cad-core';
import type { ExtrudeEndTargetDisplay } from '@puppycad/cad-core';
import {createExtrudeDragController} from './extrude-drag';
import type {
  ExtrudeTarget,
  ExtrudeViewportRef,
  SelectionObject,
  ViewportSelectionSnapshot,
} from './extrude-types';
import {
  buildExtrudePreviewRequest,
  type ExtrudePreviewRequest,
} from './build-extrude-preview-request';
import {
  applyExtrudeDirectionDragState,
  buildExtrudeDirectionDragState,
} from './extrude-direction-drag-state';
import { applyFeatureCommitMeshDelta } from './apply-feature-commit-mesh-delta';
import {
  type ContourLike,
  buildTargetFromContours,
  resolveContourLabel,
} from '../core/extrude/extrude-contour-helpers';
import { rebuildTrackedSketchContours } from '../core/sketch/rebuild-tracked-sketch-contours';

export type ExtrudePreviewLoadingState = {
  /** 是否正在等待预览体生成。 */
  active: boolean;
  /** 当前拉伸命令类型。 */
  operation: 'boss' | 'cut';
  /** 触发 loading 的预览原因。 */
  reason: 'curve-profile-preview';
};

type UseExtrudeFeatureOptions = {
  viewportRef: ExtrudeViewportRef;
  getObjectById: (objectId: string) => Promise<SelectionObject | null>;
  updateState: () => Promise<void>;
  onCleanup?: (target: ExtrudeTarget, featureId?: string) => void;
  /**
   * 预览体生成期间的 loading 回调。
   *
   * 设计说明：封闭曲线 profile 拉伸需要走 Puppy CAD 内核 曲线轮廓与实体预览链路，
   * 生成耗时明显高于普通线段轮廓，因此这里把“预览请求已入队但结果未返回”的状态
   * 上抛给 Editor，由宿主统一管理全局 loading。
   */
  onPreviewLoadingChange?: (state: ExtrudePreviewLoadingState) => void;
  message: MessageApi;
};

export type ExtrudeUiContourItem = {
  regionId: string;
  contourKey?: string;
  sketchId: string;
  label: string;
};

export type ExtrudeUiState = {
  /** 仅供属性栏显示，不持久化到特征定义。 */
  endTargetItems?: Partial<Record<'direction1' | 'direction2', ExtrudeEndTargetDisplay>>;
  /** 由相同文档范围和前缀的Worker预览提供，不通过可见对象猜测。 */
  canUseThroughAll: boolean;
  /** 当前Worker前缀是否允许首体加盖。 */
  canUseCapEnds: boolean;
  active: boolean;
  operation: 'boss' | 'cut';
  editingFeatureId: string | null;
  definition: ExtrudePropertyDefinition;
  depth: number;
  direction: 'normal' | 'reverse';
  mergeResult: boolean;
  selectedContours: ExtrudeUiContourItem[];
  canSubmit: boolean;
  previewError: string | null;
  activeEndTargetDirection: 'direction1' | 'direction2' | null;
};

const EPS = 1e-6;
const PREVIEW_THROTTLE_MS = 80;
const DEFAULT_EXTRUDE_DEPTH = convertMmToM(10);
const DEFAULT_FACE_EXTRUDE_DEPTH = convertMmToM(2);
const DEFAULT_CONTOUR_COLOR = 0xa8a8c5;
const DEFAULT_CONTOUR_OPACITY = 0.2;
const SELECTED_CONTOUR_COLOR = 0x93c5fd;
const SELECTED_CONTOUR_OPACITY = 0.34;
const HOVERED_CONTOUR_COLOR = 0x60a5fa;
const HOVERED_CONTOUR_OPACITY = 0.46;
const extrudeSceneResolver = new BossExtrudeSceneResolver();
const extrudeFaceSelectionTargetResolver = new ExtrudeFaceSelectionTargetResolver();
const endTargetResolver = new ExtrudeEndTargetResolver();

/**
 * 规范化深度值。
 * @param depth 输入深度。
 * @returns 非负深度。
 */
function normalizeDepth(depth: number): number {
  if (!Number.isFinite(depth)) {
    return DEFAULT_EXTRUDE_DEPTH;
  }
  return Math.max(EPS, Math.abs(depth));
}

/**
 * 基于当前拉伸目标构建持久化特征输入绑定。
 * @param target 拉伸目标。
 * @param scene 当前场景。
 * @returns 序列化后的特征输入绑定集合。
 */

/**
 * 判断拉伸目标是否包含曲线 profile。
 *
 * @param target 拉伸目标。
 * @returns 是否应按曲线轮廓处理拖拽预览。
 */
function isCurveProfileExtrudeTarget(target: ExtrudeTarget): boolean {
  if (hasCurveProfileParams(target.params)) {
    return true;
  }
  return Array.isArray(target.regions)
    && target.regions.some((region) => hasCurveProfileRegionGeometry(region.geometry));
}

/**
 * 拉伸会话 Hook。
 * @param options Hook 配置。
 * @returns 拉伸状态与动作。
 */
export function useExtrudeFeature(options: UseExtrudeFeatureOptions) {
  const {
    viewportRef,
    getObjectById,
    updateState,
    onCleanup,
    onPreviewLoadingChange,
    message,
  } = options;
  const isExtrudeMode = ref(false);
  const activeEndTargetDirection = ref<'direction1' | 'direction2' | null>(null);
  const endTargetItems = ref<Partial<Record<'direction1' | 'direction2', ExtrudeEndTargetDisplay>>>({});
  let endTargetDisplayVersion = 0;
  /** 目标选择临时借用的原拾取模式；跨方向切换不覆盖原值。 */
  let selectionModeBeforeEndTarget: string | null = null;
  /**
   * 退出终止目标选择时恢复命令接管前的拾取模式。
   * 轮廓激活、条件切换、确认、取消和组件释放共用此入口。
   */
  const restoreEndTargetSelectionMode = (): void => {
    activeEndTargetDirection.value = null;
    if (selectionModeBeforeEndTarget !== null) viewportRef.value?.setSelectionMode?.(selectionModeBeforeEndTarget);
    selectionModeBeforeEndTarget = null;
  };
  const extrudeOperation = ref<'boss' | 'cut'>('boss');
  const extrudeDefinition = ref<ExtrudePropertyDefinition>(ExtrudeFeatureEditDefinitionFactory.createDefault({
    featureKind: 'boss-extrude',
    operation: 'boss',
  }));
  const canSubmit = ref(false);
  const canUseThroughAll = ref(false);
  const canUseCapEnds = ref(false);
  const previewError = ref<string | null>(null);
  const selectedContoursVersion = ref(0);

  let pendingContours: ContourLike[] = [];
  let activeTarget: ExtrudeTarget | null = null;
  let highlightedContourId: string | null = null;
  let previewRequestVersion = 0;
  let previewTimer: number | null = null;
  let pendingPreview: ExtrudePreviewRequest | null = null;
  let activePreviewLoadingVersion: number | null = null;
  let keydownHandler: ((event: KeyboardEvent) => void) | null = null;
  let unsubscribeSketchDefinitionDirty: (() => void) | null = null;
  let editingFeatureId: string | null = null;
  let editingFeatureSession: FeatureEditSession | null = null;
  let finishMode: 'cancel' | 'commit' = 'cancel';

  /**
   * UI 状态。
   */
  const extrudeUiState = computed<ExtrudeUiState>(() => {
    void selectedContoursVersion.value;
    const siblingContours = [...pendingContours];
    const definition = extrudeDefinition.value;
    return {
      active: isExtrudeMode.value,
      endTargetItems: endTargetItems.value,
      canUseThroughAll: canUseThroughAll.value,
      canUseCapEnds: canUseCapEnds.value,
      activeEndTargetDirection: activeEndTargetDirection.value,
      operation: extrudeOperation.value,
      editingFeatureId,
      definition,
      depth: definition.direction1.depth,
      direction: definition.direction1.reverse ? 'reverse' : 'normal',
      mergeResult: definition.mergeResult,
      selectedContours: pendingContours.map((item) => ({
        regionId: item.id,
        contourKey: item.contourKey,
        sketchId: item.sketchId,
        label: resolveContourLabel(item, siblingContours),
      })),
      canSubmit: canSubmit.value,
      previewError: previewError.value,
    };
  });

  /**
   * 替换拉伸属性定义。
   * @param nextDefinition 新定义。
   */
  const setExtrudeDefinition = (nextDefinition: ExtrudePropertyDefinition): void => {
    const targetsChanged = (['direction1', 'direction2'] as const).some((key) => JSON.stringify(nextDefinition[key].target) !== JSON.stringify(extrudeDefinition.value[key].target));
    extrudeDefinition.value = ExtrudeFeatureEditDefinitionFactory.sanitizeDefinition(nextDefinition);
    if (targetsChanged) void refreshEndTargetDisplays();
  };

  /** 只在目标变化时查询设计树名称；纯显示数据不随拖动重复请求Worker。 */
  const refreshEndTargetDisplays = async (): Promise<void> => {
    const version = ++endTargetDisplayVersion;
    endTargetItems.value = {};
    viewportRef.value?.setExtrudeEndTargetVisual?.(null, 'clear');
    try {
      const items = await Promise.all((['direction1', 'direction2'] as const).map(async (key) => {
        const target = extrudeDefinition.value[key].target;
        return [key, target ? await viewportRef.value?.resolveExtrudeEndTargetDisplay?.(target) : null] as const;
      }));
      if (version !== endTargetDisplayVersion) return;
      endTargetItems.value = Object.fromEntries(items.filter((entry) => entry[1]));
    } catch (error) {
      if (version === endTargetDisplayVersion) message.error(error instanceof Error ? error.message : '终止目标名称解析失败。');
    }
  };

  /**
   * 关闭预览 loading。
   * @param requestVersion 指定请求版本时，仅关闭仍属于该版本的 loading。
   */
  const hidePreviewLoading = (requestVersion?: number): void => {
    if (requestVersion !== undefined && activePreviewLoadingVersion !== requestVersion) {
      return;
    }
    if (activePreviewLoadingVersion === null) {
      return;
    }
    activePreviewLoadingVersion = null;
    onPreviewLoadingChange?.({
      active: false,
      operation: extrudeOperation.value,
      reason: 'curve-profile-preview',
    });
  };

  /**
   * 为耗时的曲线 profile 预览打开 loading。
   * @param requestVersion 当前预览请求版本。
   * @param target 当前拉伸目标。
   */
  const showPreviewLoadingIfNeeded = (
    requestVersion: number,
    target: ExtrudeTarget,
  ): void => {
    if (!isCurveProfileExtrudeTarget(target)) {
      hidePreviewLoading();
      return;
    }
    activePreviewLoadingVersion = requestVersion;
    onPreviewLoadingChange?.({
      active: true,
      operation: extrudeOperation.value,
      reason: 'curve-profile-preview',
    });
  };

  /**
   * 基于当前目标解析切除默认预览种子。
   *
   * 设计说明：
   * 1. 该默认值属于 cut-extrude 的领域语义，必须从 cad-core 取真相，不能在宿主层写死；
   * 2. 仅在创建态首次建立目标时消费，编辑已有特征时必须尊重持久化定义；
   * 3. 一旦 cad-core 无法提供种子，这里回退到当前定义，不做额外猜测。
   *
   * @param target 当前拉伸目标。
   * @returns 写回默认种子后的定义。
   */
  const resolveCutPreviewSeedDefinition = (target: ExtrudeTarget): {
    definition: ExtrudePropertyDefinition;
    applied: boolean;
  } => {
    if (editingFeatureId || extrudeOperation.value !== 'cut') {
      return {
        definition: extrudeDefinition.value,
        applied: false,
      };
    }
    const seed = viewportRef.value?.resolveCutExtrudePreviewSeed?.({
      selection: target.selection,
      definition: extrudeDefinition.value,
    }) ?? null;
    if (!seed) {
      return {
        definition: extrudeDefinition.value,
        applied: false,
      };
    }
    const nextDefinition = CutExtrudeDefinitionCoordinator.applyPreviewSeed(
      extrudeDefinition.value,
      seed,
    );
    setExtrudeDefinition(nextDefinition);
    return {
      definition: extrudeDefinition.value,
      applied: true,
    };
  };

  /**
   * 结束当前拉伸会话。
   *
   * 设计说明：
   * 1. “取消编辑回滚”与“确认提交后的收尾”是两条不同链路，不能继续共用同一个关闭入口；
   * 2. 只有在 `restoreFeature=true` 且当前存在编辑会话时，才允许回放原特征结果；
   * 3. 其余 UI 状态清理统一收口到这里，避免确认提交后再次触发旧特征回滚。
   *
   * @param input 收尾配置。
   * @returns 是否实际结束了当前会话。
   */
  const finalizeExtrudeSession = (input: {
    restoreFeature: boolean;
  }): boolean => {
    restoreEndTargetSelectionMode();
    viewportRef.value?.setExtrudeEndTargetVisual?.(null, 'clear');
    if (!isExtrudeMode.value) {
      return false;
    }

    isExtrudeMode.value = false;
    if (input.restoreFeature && editingFeatureId && editingFeatureSession) {
      viewportRef.value?.closeFeatureEditSession?.(editingFeatureId);
    }

    editingFeatureId = null;
    editingFeatureSession = null;
    extrudeOperation.value = 'boss';
    finishMode = 'cancel';
    setExtrudeDefinition(ExtrudeFeatureEditDefinitionFactory.createDefault({
      featureKind: 'boss-extrude',
      operation: 'boss',
    }));
    unbindKeydown();
    dragController.stop();
    clearPreview();
    clearPendingContours();
    activeTarget = null;
    canSubmit.value = false;
    previewError.value = null;
    canUseThroughAll.value = false;
    canUseCapEnds.value = false;
    viewportRef.value?.setSketchRegionSelectionMode?.(false);
    viewportRef.value?.setExtrudePreviewMesh(null);
    return true;
  };

  /**
   * 刷新当前拉伸定义的预览。
   * @param force 是否强制刷新。
   */
  const refreshPreviewFromDefinition = (force = true): void => {
    if (!activeTarget) {
      return;
    }
    requestPreview(activeTarget, extrudeDefinition.value, force);
  };

  /**
   * 触发版本变更。
   */
  const markSelectedContoursChanged = () => {
    selectedContoursVersion.value += 1;
  };

  /**
   * 更新轮廓视觉状态。
   */
  const syncContourVisuals = (): void => {
    pendingContours.forEach((item) => {
      const object = item.sceneObject as any;
      const material = object?.material;
      if (!material || typeof material !== 'object' || !('color' in material) || !('opacity' in material)) {
        return;
      }
      material.color.setHex(
        highlightedContourId === item.id ? HOVERED_CONTOUR_COLOR : SELECTED_CONTOUR_COLOR,
      );
      material.opacity = highlightedContourId === item.id
        ? HOVERED_CONTOUR_OPACITY
        : SELECTED_CONTOUR_OPACITY;
      material.transparent = true;
    });
  };

  /**
   * 重置轮廓视觉状态。
   * @param contour 轮廓对象。
   */
  const resetContourVisual = (contour: ContourLike): void => {
    const object = contour.sceneObject as any;
    const material = object?.material;
    if (!material || typeof material !== 'object' || !('color' in material) || !('opacity' in material)) {
      return;
    }
    material.color.setHex(DEFAULT_CONTOUR_COLOR);
    material.opacity = DEFAULT_CONTOUR_OPACITY;
    material.transparent = true;
  };

  /**
   * 清空轮廓选择。
   */
  const clearPendingContours = (): void => {
    if (unsubscribeSketchDefinitionDirty) {
      unsubscribeSketchDefinitionDirty();
      unsubscribeSketchDefinitionDirty = null;
    }
    pendingContours.forEach((item) => {
      resetContourVisual(item);
    });
    pendingContours = [];
    highlightedContourId = null;
    markSelectedContoursChanged();
  };

  /**
   * 解除草图定义 dirty 订阅。
   */
  const unbindSketchDefinitionDirty = (): void => {
    if (!unsubscribeSketchDefinitionDirty) {
      return;
    }
    unsubscribeSketchDefinitionDirty();
    unsubscribeSketchDefinitionDirty = null;
  };

  /**
   * 基于场景中的最新草图轮廓重建当前已选轮廓。
   *
   * @param sketchId 草图 ID。
   * @returns 是否成功重建到至少一个轮廓。
   */
  const rebuildPendingContoursFromScene = (sketchId: string): boolean => {
    const scene = viewportRef.value?.getScene?.() ?? null;
    const sketchGroup = scene?.getObjectByProperty('uuid', sketchId) ?? null;
    if (!sketchGroup || !isSketchGroup(sketchGroup)) {
      return false;
    }

    const latestContours = extrudeSceneResolver.collectContoursFromGroup(sketchGroup) as ContourLike[];
    const rebuiltContours = rebuildTrackedSketchContours(pendingContours, latestContours);
    if (rebuiltContours.length === 0) {
      pendingContours.forEach((item) => {
        resetContourVisual(item);
      });
      pendingContours = [];
      highlightedContourId = null;
      markSelectedContoursChanged();
      activeTarget = null;
      clearPreview();
      canSubmit.value = false;
      unbindSketchDefinitionDirty();
      previewError.value = '当前草图未形成可用于拉伸的已选轮廓。';
      return false;
    }

    pendingContours.forEach((item) => {
      resetContourVisual(item);
    });
    pendingContours = rebuiltContours;
    markSelectedContoursChanged();
    syncContourVisuals();
    syncTargetWithPendingContours(true);
    return true;
  };

  /**
   * 订阅当前已选草图的 definition dirty 事件。
   */
  const bindSketchDefinitionDirty = (): void => {
    unbindSketchDefinitionDirty();

    if (!isExtrudeMode.value || pendingContours.length === 0) {
      return;
    }

    const sketchId = pendingContours[0]!.sketchId;
    const subscribeSketchDefinitionDirty = viewportRef.value?.subscribeSketchDefinitionDirty;
    if (!subscribeSketchDefinitionDirty) {
      return;
    }

    unsubscribeSketchDefinitionDirty = subscribeSketchDefinitionDirty(sketchId, () => {
      void rebuildPendingContoursFromScene(sketchId);
    });
  };

  /**
   * 将已解析轮廓合并到当前拉伸会话。
   *
   * 设计说明：
   * 1. 正常场景点击使用“切换”语义，再次点击同一轮廓会移除；
   * 2. 进入命令时吸收预选对象使用“填充”语义，只补齐当前会话，没有重复项时才追加；
   * 3. 所有真正的预览刷新都统一在这里收口，避免多入口各自维护一套 pendingContours 更新逻辑。
   *
   * @param resolvedContours 已解析轮廓集合。
   * @param options 合并选项。
   * @returns 是否实际发生变更。
   */
  const mergeResolvedContoursIntoSession = (
    resolvedContours: ContourLike[],
    options: {
      toggle: boolean;
    },
  ): boolean => {
    if (resolvedContours.length === 0) {
      return false;
    }

    const sketchId = resolvedContours[0]!.sketchId;
    if (pendingContours.length > 0 && pendingContours[0]!.sketchId !== sketchId) {
      clearPendingContours();
      message.info('多轮廓拉伸仅支持同一草图，已切换到当前草图。');
    }

    let changed = false;
    resolvedContours.forEach((contour) => {
      const existedIndex = pendingContours.findIndex((item) => item.id === contour.id);
      if (options.toggle && existedIndex >= 0) {
        resetContourVisual(pendingContours[existedIndex]!);
        pendingContours.splice(existedIndex, 1);
        changed = true;
        return;
      }

      if (existedIndex >= 0) {
        pendingContours.splice(existedIndex, 1, contour);
        changed = true;
        return;
      }

      pendingContours.push(contour);
      changed = true;
    });

    if (!changed) {
      return false;
    }

    markSelectedContoursChanged();
    syncContourVisuals();
    syncTargetWithPendingContours(true);
    return true;
  };

  /**
   * 吸收进入拉伸命令前已经存在的预选对象。
   *
   * @returns 是否成功吸收至少一个可用轮廓。
   */
  /**
   * 读取进入拉伸命令瞬间的预选对象。
   *
   * 设计说明：命令初始化过程可能清理预览、切换选择模式或移除失效对象树，
   * 因此 SolidWorks 语义中的“预选轮廓”必须在这些初始化动作之前快照。
   *
   * @returns 当前视口选择对象 ID 副本。
   */
  const readPreselectedContourObjectIds = (): string[] => {
    return viewportRef.value?.getSelectedObjectIds?.() ?? [];
  };

  const absorbPreselectedContours = (
    selectedObjectIds: readonly string[] = readPreselectedContourObjectIds(),
  ): boolean => {
    if (selectedObjectIds.length === 0) {
      return false;
    }

    let absorbed = false;
    selectedObjectIds.forEach((objectId) => {
      const resolvedContours = viewportRef.value?.resolveBossExtrudeSelectionContours?.(objectId) as BossExtrudeSceneContour[] | 'need-region' | 'open-sketch' | null | undefined;
      if (!resolvedContours || resolvedContours === 'need-region' || resolvedContours === 'open-sketch') {
        return;
      }
      if (mergeResolvedContoursIntoSession(resolvedContours, { toggle: false })) {
        absorbed = true;
      }
    });

    return absorbed;
  };

  /**
   * 清理预览。
   */
  const clearPreview = (): void => {
    dragController.syncPreviewPositions(null);
    previewRequestVersion += 1;
    if (previewTimer !== null) {
      clearTimeout(previewTimer);
      previewTimer = null;
    }
    pendingPreview = null;
    hidePreviewLoading();
    void viewportRef.value?.setExtrudePreviewMesh(null);
  };

  /**
   * 取消尚未落地的预览请求，但保留当前已经显示的预览体。
   */
  const cancelPendingPreviewRequest = (): void => {
    previewRequestVersion += 1;
    if (previewTimer !== null) {
      clearTimeout(previewTimer);
      previewTimer = null;
    }
    pendingPreview = null;
    hidePreviewLoading();
  };

  /**
   * 应用预览。
   * @param request 预览请求。
   */
  const updatePreview = async (request: ExtrudePreviewRequest): Promise<void> => {
    const depth = request.definition.direction2.enabled
      ? Math.abs(request.definition.direction1.depth) + Math.abs(request.definition.direction2.depth)
      : Math.abs(request.definition.direction1.depth);
    if (['blind', 'mid_plane'].includes(request.definition.direction1.endCondition) && depth < EPS) {
      clearPreview();
      canSubmit.value = false;
      previewError.value = '拉伸深度过小。';
      return;
    }

    try {
      if (request.version !== previewRequestVersion) {
        return;
      }
      const previewResult = await viewportRef.value?.previewExtrudeFeature?.({
        featureId: editingFeatureId,
        selection: request.selection,
        definition: request.definition,
      });
      if (!previewResult) {
        throw new Error('拉伸预览执行入口不可用。');
      }
      if (request.version !== previewRequestVersion) {
        return;
      }
      await viewportRef.value?.setExtrudePreviewMesh(previewResult.meshData, {
        prototype: previewResult.prototype,
        draftDefinition: previewResult.draftDefinition as unknown,
      });
      const previewMetadata = (previewResult.meshData as { metadata?: Record<string, unknown> } | null)?.metadata ?? null;
      if (request.version !== previewRequestVersion) return;
      if (typeof previewMetadata?.canUseThroughAll === 'boolean') canUseThroughAll.value = previewMetadata.canUseThroughAll;
      if (typeof previewMetadata?.canUseCapEnds === 'boolean') canUseCapEnds.value = previewMetadata.canUseCapEnds;
      if (previewMetadata?.previewInvalid === true) {
        dragController.syncPreviewPositions(null);
        canSubmit.value = false;
        previewError.value = typeof previewMetadata.previewInvalidMessage === 'string'
          ? previewMetadata.previewInvalidMessage
          : '拉伸预览失败。';
        return;
      }
      dragController.syncPreviewPositions((previewResult.meshData as { positions?: ArrayLike<number> } | null)?.positions ?? null);
      canSubmit.value = true;
      previewError.value = null;
    } catch (error) {
      if (request.version !== previewRequestVersion) {
        return;
      }
      clearPreview();
      canSubmit.value = false;
      previewError.value = error instanceof Error ? error.message : '拉伸预览失败。';
    } finally {
      hidePreviewLoading(request.version);
    }
  };

  /**
   * 请求预览刷新。
   * @param target 拉伸目标。
   * @param distance 深度。
   * @param force 是否强制刷新。
   */
  const requestPreview = (
    target: ExtrudeTarget,
    definition: ExtrudePropertyDefinition,
    force = false,
  ) => {
    const requestVersion = ++previewRequestVersion;
    dragController.syncPreviewPositions(null);
    pendingPreview = buildExtrudePreviewRequest({
      version: requestVersion,
      target,
      definition,
    });
    showPreviewLoadingIfNeeded(requestVersion, target);
    if (previewTimer !== null && !force) {
      return;
    }
    if (previewTimer !== null) {
      clearTimeout(previewTimer);
    }
    previewTimer = window.setTimeout(() => {
      previewTimer = null;
      const payload = pendingPreview;
      pendingPreview = null;
      if (payload) {
        void updatePreview(payload);
      }
    }, PREVIEW_THROTTLE_MS);
  };

  /**
   * 应用正式拉伸。
   * @param target 拉伸目标。
   * @param distance 深度。
   */
  const applyExtrude = async (
    target: ExtrudeTarget,
    submitDefinition: ExtrudePropertyDefinition,
  ): Promise<void> => {
    if (!canSubmit.value) {
      throw new Error(previewError.value ?? '当前拉伸参数尚未形成可提交结果。');
    }
    clearPreview();
    const commitResult = await viewportRef.value?.commitExtrudeFeature?.({
      featureId: editingFeatureId,
      selection: target.selection,
      definition: submitDefinition,
    });
    if (!commitResult) {
      throw new Error('拉伸正式提交执行入口不可用。');
    }

    if (commitResult.replaced) {
      const committedFeatureId = editingFeatureId ?? commitResult.featureId;
      finishMode = 'commit';
      editingFeatureId = null;
      editingFeatureSession = null;
      await updateState();
      onCleanup?.(target, committedFeatureId);
      return;
    }
    await applyFeatureCommitMeshDelta(viewportRef, commitResult);
    await updateState();
    onCleanup?.(target, commitResult.objectId ?? commitResult.featureId);
  };

  const dragController = createExtrudeDragController({
    viewportRef,
    message,
    onPreview: (target, state, force) => {
      requestPreview(target, applyExtrudeDirectionDragState(extrudeDefinition.value, state), force);
    },
    onApply: async (target, state) => {
      await applyExtrude(target, applyExtrudeDirectionDragState(extrudeDefinition.value, state));
    },
    onStateChange: (state) => {
      setExtrudeDefinition(applyExtrudeDirectionDragState(extrudeDefinition.value, state));
    },
    shouldSuspendPreviewWhileDragging: isCurveProfileExtrudeTarget,
    onDragPreviewSuspensionChange: (isSuspended) => {
      if (isSuspended) {
        cancelPendingPreviewRequest();
      }
    },
    onFinish: () => {
      if (finishMode === 'commit') {
        finalizeExtrudeSession({ restoreFeature: false });
        return;
      }
      finalizeExtrudeSession({ restoreFeature: true });
    },
  });

  /**
   * 启动手柄与默认预览。
   * @param target 拉伸目标。
   * @param initialDistance 初始深度。
   */
  const startTarget = (target: ExtrudeTarget, initialDistance: number): boolean => {
    activeTarget = target;
    const cutPreviewSeedState = resolveCutPreviewSeedDefinition(target);
    const currentDefinition = cutPreviewSeedState.definition;
    const nextDefinition = cutPreviewSeedState.applied
      ? currentDefinition
      : {
        ...currentDefinition,
        direction1: {
          ...currentDefinition.direction1,
          depth: normalizeDepth(initialDistance),
          reverse: initialDistance < 0,
        },
      };
    setExtrudeDefinition(nextDefinition);
    return dragController.start(target, {
      state: buildExtrudeDirectionDragState(extrudeDefinition.value),
      forcePreview: true,
    });
  };

  /**
   * 从面选择构造拉伸目标。
   * @returns 拉伸目标或空。
   */
  const buildTargetFromFaceSelection = async (): Promise<ExtrudeTarget | null> => {
    const selectionInfo = viewportRef.value?.getSelectedFaceInfo?.() ?? null;
    if (!selectionInfo) {
      return null;
    }
    const scene = viewportRef.value?.getScene() ?? null;
    const stateObject = await getObjectById(selectionInfo.objectId);
    if (!stateObject) {
      return null;
    }
    const resolvedTarget = extrudeFaceSelectionTargetResolver.resolve({
      scene,
      objectId: selectionInfo.objectId,
      faceIndex: selectionInfo.faceIndex,
      sourceType: stateObject.type === 'face' ? 'face' : 'solid_face',
      getSceneObjectById: viewportRef.value?.getSceneObjectById,
    });
    if (!resolvedTarget) {
      message.warning('当前仅支持拉伸平面面。');
      return null;
    }
    return {
      ...resolvedTarget.target,
    };
  };

  /**
   * 解析单次选择对应的轮廓。
   * @param meshId 对象 ID。
   * @returns 轮廓集合或状态。
   */

  /**
   * 同步基于轮廓的拉伸目标。
   * @param forcePreview 是否强制刷新预览。
   */
  const syncTargetWithPendingContours = (forcePreview = true): void => {
    if (pendingContours.length === 0) {
      activeTarget = null;
      unbindSketchDefinitionDirty();
      dragController.clearSelection();
      clearPreview();
      canSubmit.value = false;
      previewError.value = '请选择闭合轮廓。';
      return;
    }
    const target = buildTargetFromContours(pendingContours);
    activeTarget = target;
    bindSketchDefinitionDirty();
    if (!dragController.updateTarget(target, {
      state: buildExtrudeDirectionDragState(extrudeDefinition.value),
      forcePreview,
    })) {
      const direction1 = extrudeDefinition.value.direction1;
      startTarget(target, direction1.reverse ? -direction1.depth : direction1.depth);
    }
  };

  /**
   * 绑定键盘事件。
   */
  const bindKeydown = (): void => {
    if (keydownHandler) {
      return;
    }
    keydownHandler = (event: KeyboardEvent) => {
      if (!isExtrudeMode.value) {
        return;
      }
      if (event.key === 'Escape') {
        cancelExtrude();
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    };
    window.addEventListener('keydown', keydownHandler, true);
  };

  /**
   * 解绑键盘事件。
   */
  const unbindKeydown = (): void => {
    if (!keydownHandler) {
      return;
    }
    window.removeEventListener('keydown', keydownHandler, true);
    keydownHandler = null;
  };

  /**
   * 启动拉伸命令。
   *
   * @param preselectedContourObjectIds 命令启动边界已经稳定下来的预选轮廓对象 ID。
   */
  const handleExtrudeCommand = async (
    preselectedContourObjectIds: readonly string[] = readPreselectedContourObjectIds(),
  ): Promise<void> => {
    extrudeOperation.value = 'boss';
    isExtrudeMode.value = true;
    editingFeatureId = null;
    editingFeatureSession = null;
    finishMode = 'cancel';
    setExtrudeDefinition({
      ...ExtrudeFeatureEditDefinitionFactory.createDefault({
        featureKind: 'boss-extrude',
        operation: 'boss',
      }),
      selectedContoursInputActive: true,
    });
    canSubmit.value = false;
    previewError.value = '请选择闭合轮廓。';
    clearPendingContours();
    clearPreview();
    viewportRef.value?.enableHover?.('crosshair');
    viewportRef.value?.setSketchRegionSelectionMode?.(true);
    bindKeydown();
    if (absorbPreselectedContours(preselectedContourObjectIds)) {
      return;
    }
    message.info('请选择草图轮廓、矩形或其他闭合草图实体。');
  };

  /**
   * 启动拉伸切除命令。
   *
   * @param preselectedContourObjectIds 命令启动边界已经稳定下来的预选轮廓对象 ID。
   */
  const handleCutExtrudeCommand = async (
    preselectedContourObjectIds: readonly string[] = readPreselectedContourObjectIds(),
  ): Promise<void> => {
    extrudeOperation.value = 'cut';
    isExtrudeMode.value = true;
    editingFeatureId = null;
    editingFeatureSession = null;
    finishMode = 'cancel';
    setExtrudeDefinition({
      ...ExtrudeFeatureEditDefinitionFactory.createDefault({
        featureKind: 'cut-extrude',
        operation: 'cut',
      }),
      selectedContoursInputActive: true,
    });
    canSubmit.value = false;
    previewError.value = '请选择用于切除的闭合轮廓。';
    clearPendingContours();
    clearPreview();
    viewportRef.value?.enableHover?.('crosshair');
    viewportRef.value?.setSketchRegionSelectionMode?.(true);
    bindKeydown();
    if (absorbPreselectedContours(preselectedContourObjectIds)) {
      return;
    }
    message.info('请选择要切除的草图轮廓、矩形或其他闭合草图实体。');
  };

  /**
   * 处理视口选择变化。
   * @param meshId 对象 ID。
   * @returns 是否已处理。
   */
  /**
   * 重新进入已有拉伸特征的编辑态。
   *
   * @param featureId 特征 ID。
   * @returns 是否成功进入编辑。
   */
  const startEditingExtrudeFeature = async (featureId: string): Promise<boolean> => {
    let restoredDefinition: ExtrudePropertyDefinition | null = null;
    let restoredContours: ContourLike[] = [];
    let editingSession: FeatureEditSession | null = null;

    if (viewportRef.value?.startExtrudeEditSession) {
      const payload = await viewportRef.value.startExtrudeEditSession(featureId);
      editingSession = payload.session;
      restoredDefinition = payload.definition;
      restoredContours = payload.contours as BossExtrudeSceneContour[];
    }

    if (!restoredDefinition || restoredContours.length === 0) {
      return false;
    }

    if (restoredContours.length === 0) {
      if (editingSession) {
        viewportRef.value?.closeFeatureEditSession?.(featureId);
      }
      message.warning('当前拉伸特征的所选轮廓无法恢复，暂时不能进入编辑。');
      return false;
    }

    isExtrudeMode.value = true;
    extrudeOperation.value = restoredDefinition.operation;
    editingFeatureId = featureId;
    editingFeatureSession = editingSession;
    finishMode = 'cancel';
    canSubmit.value = false;
    previewError.value = null;
    clearPendingContours();
    clearPreview();
    viewportRef.value?.enableHover?.('crosshair');
    viewportRef.value?.setSketchRegionSelectionMode?.(true);
    bindKeydown();

    setExtrudeDefinition({
      ...restoredDefinition,
      selectedContoursInputActive: true,
    });
    pendingContours = restoredContours;
    markSelectedContoursChanged();
    syncContourVisuals();
    syncTargetWithPendingContours(true);
    return true;
  };

  const handleExtrudeSelectionChange = async (selection: ViewportSelectionSnapshot | string | null): Promise<boolean> => {
    if (syncingEndTargetVisual) return true;
    const snapshot = selection && typeof selection !== 'string' ? selection : null;
    const meshId = typeof selection === 'string' ? selection : snapshot?.objectId ?? null;
    if (!isExtrudeMode.value || !meshId) {
      return false;
    }

    if (activeEndTargetDirection.value) {
      const directionKey = activeEndTargetDirection.value;
      const direction = extrudeDefinition.value[directionKey];
      const selected = snapshot?.object ?? viewportRef.value?.getPrimarySelectedObject?.()
        ?? viewportRef.value?.getSceneObjectById?.(meshId);
      const target = endTargetResolver.resolve(selected, direction.endCondition, snapshot?.topologyPick);
      if (!target) {
        message.info(direction.endCondition === 'up_to_vertex' ? '请选择终止顶点。' : direction.endCondition === 'up_to_body' ? '请选择终止实体或曲面体。' : '请选择终止面。');
        return true;
      }
      setExtrudeDefinition({ ...extrudeDefinition.value, [directionKey]: { ...direction, target } });
      refreshPreviewFromDefinition(true);
      return true;
    }

    if (!extrudeDefinition.value.selectedContoursInputActive) {
      return false;
    }

    if (viewportRef.value?.getSelectionMode?.() === 'face') {
      const faceTarget = await buildTargetFromFaceSelection();
      if (faceTarget) {
        activeTarget = faceTarget;
        const initialDistance = extrudeDefinition.value.direction1.reverse
          ? -DEFAULT_FACE_EXTRUDE_DEPTH
          : DEFAULT_FACE_EXTRUDE_DEPTH;
        startTarget(faceTarget, initialDistance);
        return true;
      }
    }

    const resolvedContours = viewportRef.value?.resolveBossExtrudeSelectionContours?.(meshId) as BossExtrudeSceneContour[] | 'need-region' | 'open-sketch' | null | undefined;
    if (resolvedContours === 'need-region') {
      message.info('检测到多个闭合轮廓，请直接点击目标轮廓区域。');
      return true;
    }
    if (resolvedContours === 'open-sketch') {
      message.warning(
        extrudeOperation.value === 'cut'
          ? '当前草图未形成闭合轮廓，无法进行拉伸切除。'
          : '当前草图未形成闭合轮廓，无法进行实体拉伸。',
      );
      return true;
    }
    if (!resolvedContours || resolvedContours.length === 0) {
      return false;
    }

    const sketchId = resolvedContours[0]!.sketchId;
    if (pendingContours.length > 0 && pendingContours[0]!.sketchId !== sketchId) {
      clearPendingContours();
      message.info('多轮廓拉伸仅支持同一草图，已切换到当前草图。');
    }

    resolvedContours.forEach((contour) => {
      const existedIndex = pendingContours.findIndex((item) => item.id === contour.id);
      if (existedIndex >= 0) {
        resetContourVisual(pendingContours[existedIndex]!);
        pendingContours.splice(existedIndex, 1);
      } else {
        pendingContours.push(contour);
      }
    });
    markSelectedContoursChanged();
    syncContourVisuals();
    syncTargetWithPendingContours(true);
    return true;
  };

  /**
   * 提交拉伸。
   */
  const submitExtrude = async (): Promise<void> => {
    await (dragController.submit?.() ?? Promise.resolve(false));
  };

  /**
   * 取消拉伸。
   * @returns 是否实际取消。
   */
  const cancelExtrude = (): boolean => {
    return finalizeExtrudeSession({
      restoreFeature: true,
    });
  };

  /**
   * 设置拉伸深度。
   * @param depth 深度值。
   */
  const setExtrudeDepth = (depth: number): void => {
    const currentDefinition = extrudeDefinition.value;
    const nextDefinition = {
      ...currentDefinition,
      direction1: {
        ...currentDefinition.direction1,
        depth: normalizeDepth(depth),
      },
    };
    setExtrudeDefinition(nextDefinition);
    dragController.syncState(buildExtrudeDirectionDragState(extrudeDefinition.value), {
      forcePreview: true,
    });
  };

  /**
   * 设置拉伸方向。
   * @param direction 方向。
   */
  const setExtrudeDirection = (direction: 'normal' | 'reverse'): void => {
    const currentDefinition = extrudeDefinition.value;
    const nextDefinition = {
      ...currentDefinition,
      direction1: {
        ...currentDefinition.direction1,
        reverse: direction === 'reverse',
      },
    };
    setExtrudeDefinition(nextDefinition);
    dragController.syncState(buildExtrudeDirectionDragState(extrudeDefinition.value), {
      forcePreview: true,
    });
  };

  /**
   * 设置合并结果。
   * @param mergeResult 是否合并。
   */
  const setExtrudeMergeResult = (mergeResult: boolean): void => {
    setExtrudeDefinition({
      ...extrudeDefinition.value,
      mergeResult,
    });
    refreshPreviewFromDefinition(true);
  };

  /**
   * 设置方向 1 定义。
   * @param value 新方向定义。
   */
  const setExtrudeDirection1 = (value: ExtrudePropertyDefinition['direction1']): void => {
    const conditionChanged = value.endCondition !== extrudeDefinition.value.direction1.endCondition;
    const nextDefinition = {
      ...extrudeDefinition.value,
      direction1: {
        ...value,
      },
      direction2: value.endCondition === 'mid_plane' ? { ...extrudeDefinition.value.direction2, enabled: false } : extrudeDefinition.value.direction2,
    } as ExtrudePropertyDefinition;
    setExtrudeDefinition(nextDefinition);
    if (conditionChanged) setExtrudeEndTargetInputActive(['blind', 'mid_plane', 'through_all'].includes(value.endCondition) ? null : 'direction1');
    dragController.syncState(buildExtrudeDirectionDragState(extrudeDefinition.value), { forcePreview: true });
  };

  /**
   * 设置方向 2 定义。
   * @param value 新方向定义。
   */
  const setExtrudeDirection2 = (value: ExtrudePropertyDefinition['direction2']): void => {
    const conditionChanged = value.endCondition !== extrudeDefinition.value.direction2.endCondition;
    const nextDefinition = {
      ...extrudeDefinition.value,
      direction2: {
        ...value,
        reverse: false,
      },
    } as ExtrudePropertyDefinition;
    setExtrudeDefinition(nextDefinition);
    if (conditionChanged) setExtrudeEndTargetInputActive(['blind', 'mid_plane', 'through_all'].includes(value.endCondition) ? null : 'direction2');
    dragController.syncState(buildExtrudeDirectionDragState(extrudeDefinition.value), { forcePreview: true });
  };

  /**
   * 设置拉伸切除专属定义。
   * @param value 新的切除定义。
   */
  const setExtrudeCutOptions = (
    value: ExtrudePropertyDefinition['cutOptions'],
  ): void => {
    setExtrudeDefinition({
      ...extrudeDefinition.value,
      cutOptions: ExtrudeCutDefinitionSnapshotBuilder.clone(value),
    });
    dragController.syncState(buildExtrudeDirectionDragState(extrudeDefinition.value), {
      forcePreview: true,
    });
  };

  /**
   * 设置薄壁特征定义。
   * @param value 新薄壁定义。
   */
  const setExtrudeThinFeature = (value: ExtrudePropertyDefinition['thinFeature']): void => {
    setExtrudeDefinition({
      ...extrudeDefinition.value,
      thinFeature: {
        ...value,
      },
    });
    refreshPreviewFromDefinition(true);
  };

  /**
   * 设置所选轮廓输入框激活态。
   * @param active 是否激活。
   */
  const setExtrudeSelectedContoursInputActive = (active: boolean): void => {
    if (active) restoreEndTargetSelectionMode();
    setExtrudeDefinition({
      ...extrudeDefinition.value,
      selectedContoursInputActive: active,
    });
  };

  /**
   * 激活独立方向的终止目标选择，选择类型由已选条件决定。
   * @param directionKey 目标方向；null 表示退出目标选择。
   */
  const setExtrudeEndTargetInputActive = (directionKey: 'direction1' | 'direction2' | null): void => {
    if (!directionKey) {
      restoreEndTargetSelectionMode();
      return;
    }
    if (selectionModeBeforeEndTarget === null) selectionModeBeforeEndTarget = viewportRef.value?.getSelectionMode?.() ?? 'object';
    activeEndTargetDirection.value = directionKey;
    setExtrudeDefinition({ ...extrudeDefinition.value, selectedContoursInputActive: false });
    viewportRef.value?.setSketchRegionSelectionMode?.(false);
    const condition = extrudeDefinition.value[directionKey].endCondition;
    viewportRef.value?.setSelectionMode?.(condition === 'up_to_vertex' ? 'vertex' : condition === 'up_to_body' ? 'object' : 'face');
  };

  let syncingEndTargetVisual = false;
  /** 属性行的回选和悬停只改变交互状态，不能重新吸收为建模输入。 */
  const setExtrudeEndTargetVisual = (directionKey: 'direction1' | 'direction2', mode: 'select' | 'hover' | 'clear'): void => {
    const target = extrudeDefinition.value[directionKey].target ?? null;
    syncingEndTargetVisual = true;
    try { viewportRef.value?.setExtrudeEndTargetVisual?.(target, mode); }
    finally { syncingEndTargetVisual = false; }
  };

  /**
   * 移除已选轮廓。
   * @param regionId 轮廓 ID。
   */
  const removeExtrudeSelectedContour = (regionId: string): void => {
    const nextIndex = pendingContours.findIndex((item) => item.id === regionId);
    if (nextIndex < 0) {
      return;
    }
    resetContourVisual(pendingContours[nextIndex]!);
    pendingContours.splice(nextIndex, 1);
    markSelectedContoursChanged();
    syncContourVisuals();
    syncTargetWithPendingContours(true);
  };

  /**
   * 高亮指定轮廓。
   * @param regionId 轮廓 ID。
   */
  const highlightExtrudeSelectedContour = (regionId: string | null): void => {
    highlightedContourId = regionId;
    syncContourVisuals();
  };

  if (getCurrentInstance()) {
    onUnmounted(() => {
      cancelExtrude();
    });
  }

  return {
    isExtrudeMode,
    extrudeUiState,
    handleExtrudeCommand,
    handleCutExtrudeCommand,
    startEditingExtrudeFeature,
    handleExtrudeSelectionChange,
    cancelExtrude,
    submitExtrude,
    setExtrudeDepth,
    setExtrudeDirection,
    setExtrudeDirection1,
    setExtrudeEndTargetInputActive,
    setExtrudeEndTargetVisual,
    setExtrudeDirection2,
    setExtrudeCutOptions,
    setExtrudeThinFeature,
    setExtrudeMergeResult,
    setExtrudeSelectedContoursInputActive,
    removeExtrudeSelectedContour,
    highlightExtrudeSelectedContour,
  };
}
